import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import * as sessionReplay from '@amplitude/session-replay-browser';
import { AnalyticsBrowser } from '@segment/analytics-next';

import { AMPLITUDE_API_KEY, ENVIRONMENT } from 'assets/env_constants';
import { getImpersonateId } from 'store/user';

export const useSessionReplay = (
  analyticsBrowser: AnalyticsBrowser | null,
  isEnabled: boolean,
) => {
  const impersonateId = useSelector(getImpersonateId);
  const hasInitialized = useRef(false);
  const isActiveRef = useRef(false);
  // Bootstrap value only - matches the format Segment's own Amplitude session
  // plugin uses for a brand-new session (new Date().getTime()). Corrected by
  // the middleware below the moment a real event carries the authoritative
  // integrations['Actions Amplitude'].session_id.
  const currentSessionIdRef = useRef(Date.now());
  // First real session_id is adopted unconditionally; the > guard only
  // applies after that, to detect rotation without regressing on a
  // late/out-of-order event.
  const hasSyncedSessionIdRef = useRef(false);
  const registeredBrowserRef = useRef<AnalyticsBrowser | null>(null);
  // The exact deviceId handed to sessionReplay.init(). Segment's Amplitude
  // (Actions) destination reads device_id from context.device.id first,
  // falling back to anonymousId only if that's absent - rather than rely on
  // that fallback, the middleware below stamps this same id directly onto
  // every outgoing event so it can never diverge from what Session Replay used.
  const deviceIdRef = useRef<string | undefined>(undefined);

  // Registers the Segment middleware exactly once per analyticsBrowser
  // instance. Segment has no API to remove a middleware once added, so this
  // must depend only on the browser's identity, never on isEnabled or
  // impersonateId, or every flag/impersonation change would stack another
  // permanent handler. isActiveRef (set by the effect below) gates whether
  // the middleware actually does anything at call time.
  useEffect(() => {
    if (!analyticsBrowser) return;
    if (registeredBrowserRef.current === analyticsBrowser) return;
    registeredBrowserRef.current = analyticsBrowser;

    analyticsBrowser.addSourceMiddleware(({ payload, next }) => {
      try {
        if (!isActiveRef.current) return;

        const amplitudeIntegration = payload.obj.integrations?.[
          'Actions Amplitude'
        ] as { session_id?: number } | undefined;
        const nextSessionId = amplitudeIntegration?.session_id ?? 0;

        if (
          nextSessionId &&
          (!hasSyncedSessionIdRef.current ||
            nextSessionId > currentSessionIdRef.current)
        ) {
          hasSyncedSessionIdRef.current = true;
          currentSessionIdRef.current = nextSessionId;
          sessionReplay.setSessionId(nextSessionId);
        }

        payload.obj.properties = {
          ...payload.obj.properties,
          ...sessionReplay.getSessionReplayProperties(),
        };

        if (deviceIdRef.current) {
          payload.obj.context = {
            ...payload.obj.context,
            device: {
              ...(payload.obj.context as { device?: object } | undefined)
                ?.device,
              id: deviceIdRef.current,
            },
          };
        }
      } catch (error) {
        // Don't let a Session Replay error break the underlying Segment event pipeline.
        console.error('[SessionReplay] middleware error', error);
      } finally {
        next(payload);
      }
    });
  }, [analyticsBrowser]);

  useEffect(() => {
    if (!analyticsBrowser || !AMPLITUDE_API_KEY || !isEnabled) return;
    if (impersonateId) return;
    if (ENVIRONMENT !== 'production') return;
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    let isCancelled = false;

    (async () => {
      const user = await analyticsBrowser.user();
      if (isCancelled) return;

      deviceIdRef.current = user.anonymousId() ?? undefined;

      await sessionReplay.init(AMPLITUDE_API_KEY, {
        deviceId: deviceIdRef.current,
        sessionId: currentSessionIdRef.current,
        sampleRate: 1,
        privacyConfig: {
          defaultMaskLevel: 'conservative',
        },
      }).promise;

      if (isCancelled) return;
      isActiveRef.current = true;
    })();

    return () => {
      isCancelled = true;
      isActiveRef.current = false;
      sessionReplay.shutdown();
      hasInitialized.current = false;
    };
  }, [analyticsBrowser, isEnabled, impersonateId]);
};
